import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchItem } from "../itemApi";
import {
  emitCartUpdated,
  getDeliveryLabel,
  getOfferHighlights,
  getProductBadge,
  getResolvedProductType,
  getShippingPreview,
  readCustomer,
} from "../utils/marketplace";
import { trackAddToCart, trackBeginCheckout, trackViewItem } from "../utils/analytics";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetchItem(id);
        const product = response.data || response;
        setItem(product);
        setQty(product.stockQty > 0 ? 1 : 0);
      } catch (err) {
        setError(err.response?.data?.error || "This product is not available right now.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (item?._id) {
      trackViewItem(item);
    }
  }, [item]);

  const requireCustomer = (redirectTo) => {
    const customer = readCustomer();
    if (!customer?.token) {
      sessionStorage.setItem("redirectAfterLogin", redirectTo);
      navigate("/customer-login");
      return false;
    }
    return true;
  };

  const buildCartItem = () => ({
    _id: item._id,
    name: item.name,
    price: item.price,
    qty,
    image: item.imageUrl || item.image,
    productType: getResolvedProductType(item),
    category: item.category,
    shippingRates: item.shippingRates,
    sellerName: item.sellerName || item.sellerId?.storeName || "",
    stockQty: item.stockQty,
  });

  const addToCart = () => {
    if (!requireCustomer(`/product/${item._id}`)) return;
    if (!item || item.stockQty <= 0) return;

    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const index = cart.findIndex((row) => row._id === item._id);

    if (index >= 0) {
      const nextQty = Number(cart[index].qty || 1) + qty;
      if (nextQty > item.stockQty) {
        alert(`Only ${item.stockQty} available`);
        return;
      }
      cart[index].qty = nextQty;
    } else {
      cart.push(buildCartItem());
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    emitCartUpdated();
    trackAddToCart(item, qty);
    alert("Added to cart");
  };

  const buyNow = () => {
    if (!requireCustomer("checkout")) return;
    if (!item || item.stockQty <= 0) return;
    const buyNowItem = buildCartItem();
    localStorage.setItem("buy-now-item", JSON.stringify(buyNowItem));
    trackBeginCheckout({
      items: [buyNowItem],
      value: Number(buyNowItem.price || 0) * Number(buyNowItem.qty || 1),
      source: "buy_now",
    });
    navigate("/checkout");
  };

  if (loading) return <div style={page}>Loading product...</div>;
  if (error || !item) {
    return (
      <div style={page}>
        <h2>Product unavailable</h2>
        <p>{error || "This product cannot be opened right now."}</p>
        <Link to="/">Back to storefront</Link>
      </div>
    );
  }

  const isOutOfStock = Number(item.stockQty || 0) <= 0;
  const offerHighlights = getOfferHighlights(item);

  return (
    <main style={page}>
      <Link to="/" style={backLink}>Back to products</Link>
      <section style={detailGrid}>
        <div style={imagePanel}>
          {item.imageUrl || item.image ? (
            <img
              src={item.imageUrl || item.image}
              alt={item.name}
              style={mainImage}
            />
          ) : (
            <div style={imageFallback}>No image</div>
          )}
        </div>

        <div style={infoPanel}>
          <div style={badgeRow}>
            <span style={badge}>{getProductBadge(item)}</span>
            <span style={badge}>{item.category || "Product"}</span>
            {item.sellerName || item.sellerId?.storeName ? (
              <span style={badge}>{item.sellerName || item.sellerId?.storeName}</span>
            ) : null}
          </div>

          <h1 style={{ margin: "14px 0 8px" }}>{item.name}</h1>
          <div style={price}>₹{Number(item.price || 0).toFixed(2)}</div>
          <p style={description}>{item.description || "Product details will be updated soon."}</p>

          {offerHighlights.length ? (
            <div style={offerPanel}>
              <strong>Today's offers</strong>
              {offerHighlights.map((offer) => (
                <span key={`${offer.badge}-${offer.text}`} style={offerRow}>
                  <b>{offer.badge}</b>
                  <small>{offer.text}</small>
                </span>
              ))}
            </div>
          ) : null}

          <div style={facts}>
            <div>
              <strong>Delivery</strong>
              <span>{getDeliveryLabel(item)}</span>
            </div>
            <div>
              <strong>Shipping</strong>
              <span>From ₹{getShippingPreview(item)}</span>
            </div>
            <div>
              <strong>Stock</strong>
              <span>{isOutOfStock ? "Out of stock" : `${item.stockQty} available`}</span>
            </div>
          </div>

          {item.videoUrl ? (
            <a href={item.videoUrl} target="_blank" rel="noreferrer" style={videoLink}>
              Watch product video
            </a>
          ) : null}

          <div style={qtyRow}>
            <button type="button" onClick={() => setQty((value) => Math.max(1, value - 1))} disabled={qty <= 1}>
              -
            </button>
            <strong>{qty}</strong>
            <button
              type="button"
              onClick={() => setQty((value) => Math.min(item.stockQty, value + 1))}
              disabled={qty >= item.stockQty}
            >
              +
            </button>
          </div>

          <div style={actionRow}>
            <button type="button" onClick={addToCart} disabled={isOutOfStock} style={secondaryButton}>
              Add to Cart
            </button>
            <button type="button" onClick={buyNow} disabled={isOutOfStock} style={primaryButton}>
              Buy Now
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

const page = {
  width: "min(1120px, calc(100% - 32px))",
  margin: "32px auto",
};
const backLink = { display: "inline-block", marginBottom: 18, color: "#0f766e", fontWeight: 800 };
const detailGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(280px, 1fr) minmax(280px, 0.9fr)",
  gap: 24,
};
const imagePanel = {
  background: "#fff",
  borderRadius: 24,
  padding: 18,
  border: "1px solid #dbeafe",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};
const mainImage = {
  width: "100%",
  maxHeight: 560,
  objectFit: "cover",
  borderRadius: 18,
};
const imageFallback = {
  height: 360,
  display: "grid",
  placeItems: "center",
  background: "#f1f5f9",
  borderRadius: 18,
};
const infoPanel = {
  background: "#fff",
  borderRadius: 24,
  padding: 24,
  border: "1px solid #dbeafe",
  boxShadow: "0 18px 45px rgba(15,23,42,0.08)",
};
const badgeRow = { display: "flex", flexWrap: "wrap", gap: 8 };
const badge = {
  padding: "6px 10px",
  borderRadius: 999,
  background: "#ecfeff",
  color: "#0f766e",
  fontWeight: 800,
  fontSize: 12,
};
const price = { fontSize: 30, fontWeight: 900, color: "#0f172a", marginBottom: 14 };
const description = { color: "#475569", lineHeight: 1.7 };
const offerPanel = {
  display: "grid",
  gap: 8,
  margin: "16px 0",
  padding: 14,
  borderRadius: 16,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  fontWeight: 800,
};
const offerRow = {
  display: "grid",
  gap: 4,
  padding: "10px 12px",
  borderRadius: 14,
  background: "#fff",
  border: "1px solid #fed7aa",
};
const facts = {
  display: "grid",
  gap: 10,
  margin: "20px 0",
};
const qtyRow = { display: "flex", alignItems: "center", gap: 12, margin: "18px 0" };
const actionRow = { display: "flex", gap: 12, flexWrap: "wrap" };
const primaryButton = {
  flex: "1 1 180px",
  padding: "13px 18px",
  borderRadius: 14,
  border: "none",
  background: "#0f766e",
  color: "#fff",
  fontWeight: 900,
};
const secondaryButton = {
  ...primaryButton,
  background: "#0f172a",
};
const videoLink = {
  display: "inline-block",
  color: "#1d4ed8",
  fontWeight: 800,
  marginTop: 6,
};

export default ProductDetail;
